import { Box, Group, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { CollaborationStatus } from "@/components/shared/constants/general.const";

// ─── STEP DEFINITIONS ─────────────────────────────────────────────────────────
const STEPS = [
  { key: "request", label: "Request" },
  { key: "accepted", label: "Accepted" },
  { key: "payment", label: "Payment" },
  { key: "active", label: "Active" },
];

// ─── MAP STATUS → STEPPER STATE ───────────────────────────────────────────────
// currentStep: which step is "now" (0-indexed)
// terminal: null | "declined" | "expired"  — locks the stepper and shows a badge
// paymentNote: optional sub-label under the Payment node
type StepperState = {
  currentStep: number;
  terminal: null | "declined" | "expired";
  paymentNote: string | null;
};

function getStepperState(
  status: string,
  isRequester: boolean,
): StepperState {
  switch (status) {
    case CollaborationStatus.REQUESTED:
      return { currentStep: 0, terminal: null, paymentNote: null };

    case CollaborationStatus.BOTH_ACCEPTED:
      return { currentStep: 1, terminal: null, paymentNote: null };

    case CollaborationStatus.REQUESTER_PAID_ONLY:
      return {
        currentStep: 2,
        terminal: null,
        paymentNote: isRequester ? "Waiting on them" : "Your turn",
      };

    case CollaborationStatus.RECEIVER_PAID_ONLY:
      return {
        currentStep: 2,
        terminal: null,
        paymentNote: isRequester ? "Your turn" : "Waiting on them",
      };

    case CollaborationStatus.BOTH_PAID:
      return { currentStep: 4, terminal: null, paymentNote: null };

    case CollaborationStatus.PAYMENT_EXPIRED:
      return { currentStep: 2, terminal: "expired", paymentNote: null };

    case "DECLINED":
      return { currentStep: 0, terminal: "declined", paymentNote: null };

    default:
      return { currentStep: 0, terminal: null, paymentNote: null };
  }
}

// ─── NODE COLOUR LOGIC ────────────────────────────────────────────────────────
type NodeVariant = "completed" | "active" | "future" | "declined" | "expired";

function getNodeVariant(index: number, currentStep: number, terminal: StepperState["terminal"]): NodeVariant {
  if (terminal === "declined" && index === 0) return "declined";
  if (terminal === "expired" && index === 2) return "expired";
  if (index < currentStep) return "completed";
  if (index === currentStep) return "active";
  return "future";
}

const NODE_STYLES: Record<NodeVariant, { bg: string; border: string; text: string; shadow?: string }> = {
  completed: { bg: "var(--mantine-color-green-6)", border: "var(--mantine-color-green-6)", text: "#fff" },
  active: {
    bg: "var(--mantine-color-primary-6)",
    border: "var(--mantine-color-primary-6)",
    text: "#fff",
    shadow: "0 0 0 3px var(--mantine-color-primary-1)",
  },
  future: { bg: "#fff", border: "var(--mantine-color-gray-3)", text: "var(--mantine-color-gray-5)" },
  declined: { bg: "var(--mantine-color-red-6)", border: "var(--mantine-color-red-6)", text: "#fff" },
  expired: { bg: "var(--mantine-color-orange-6)", border: "var(--mantine-color-orange-6)", text: "#fff" },
};

const LABEL_COLORS: Record<NodeVariant, string> = {
  completed: "var(--mantine-color-green-7)",
  active: "var(--mantine-color-primary-7)",
  future: "var(--mantine-color-gray-5)",
  declined: "var(--mantine-color-red-7)",
  expired: "var(--mantine-color-orange-7)",
};

function connectorColor(index: number, currentStep: number, terminal: StepperState["terminal"]): string {
  if (terminal === "declined") return "var(--mantine-color-gray-3)";
  if (terminal === "expired" && index >= 2) return "var(--mantine-color-gray-3)";
  if (index < currentStep) return "var(--mantine-color-green-6)";
  return "var(--mantine-color-gray-3)";
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
interface CollaborationStepperProps {
  status: string;
  isRequester: boolean;
  requesterPaid: boolean;
  receiverPaid: boolean;
}

export default function CollaborationStepper({
  status,
  isRequester,
}: CollaborationStepperProps) {
  const { currentStep, terminal, paymentNote } = getStepperState(status, isRequester);

  return (
    <Box w="100%" py={4}>
      {/* Main stepper row */}
      <Group align="center" gap={0} w="100%" wrap="nowrap">
        {STEPS.map((step, i) => {
          const variant = getNodeVariant(i, currentStep, terminal);
          const style = NODE_STYLES[variant];

          return (
            <Box
              key={step.key}
              style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : "none" }}
            >
              {/* Node + label */}
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* Circle */}
                <Box
                  w={30}
                  h={30}
                  style={{
                    borderRadius: "50%",
                    backgroundColor: style.bg,
                    border: `2.5px solid ${style.border}`,
                    boxShadow: style.shadow || "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                >
                  {variant === "completed" ? (
                    <IconCheck size={15} color="#fff" stroke={3} />
                  ) : (
                    <Text fz={13} fw={700} c={style.text} lh={1}>
                      {i + 1}
                    </Text>
                  )}
                </Box>

                {/* Label */}
                <Text
                  fz={10}
                  fw={600}
                  mt={5}
                  c={LABEL_COLORS[variant]}
                  style={{ textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}
                >
                  {step.label}
                </Text>

                {/* Payment sub-note */}
                {i === 2 && paymentNote && (
                  <Text fz={9} fw={500} mt={1} c="orange.6" style={{ whiteSpace: "nowrap" }}>
                    {paymentNote}
                  </Text>
                )}
              </Box>

              {/* Connector line between nodes (not after last) */}
              {i < STEPS.length - 1 && (
                <Box
                  style={{
                    flex: 1,
                    height: 2.5,
                    borderRadius: 2,
                    backgroundColor: connectorColor(i, currentStep, terminal),
                    margin: "0 5px",
                    marginBottom: paymentNote ? 28 : 18, // offset to align with circle centres
                    transition: "background-color 0.3s ease",
                  }}
                />
              )}
            </Box>
          );
        })}
      </Group>

      {/* Terminal badge — sits below the stepper, left-aligned */}
      {terminal && (
        <Box mt={8}>
          <Text
            fz={11}
            fw={700}
            c={terminal === "declined" ? "red.7" : "orange.7"}
            bg={terminal === "declined" ? "red.0" : "orange.0"}
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: 20,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {terminal === "declined" ? "Declined" : "Payment Expired"}
          </Text>
        </Box>
      )}
    </Box>
  );
}
